import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  Select,
  SelectItem,
  Chip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import useUserStore from 'common/src/stores/useUserStore';
import useDataStore from 'common/src/stores/useDataStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useExporterLabelsStore, {
  ExporterLabelRecord,
  LabelCategory,
} from '../../store/useExporterLabelsStore';
import { LoadingState } from '../ui';

interface ExporterLabelsSectionProps {
  speciesId: number;
  season: string;
}

const CATEGORY_OPTIONS: LabelCategory[] = ['PREMIUM', 'ESTANDAR', 'REGULAR'];

export const ExporterLabelsSection = ({
  speciesId,
  season,
}: ExporterLabelsSectionProps) => {
  const { t } = useTranslation('settings');
  const { userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { labels, loading, fetchLabels, addLabel, updateLabel, deleteLabel } =
    useExporterLabelsStore();
  const [editingLabel, setEditingLabel] = useState<ExporterLabelRecord | null>(
    null
  );
  const [newLabelName, setNewLabelName] = useState('');
  const [newCategory, setNewCategory] = useState<LabelCategory>('ESTANDAR');
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const canLoad = useMemo(
    () => Boolean(userProfile?.exporter_id && speciesId && season),
    [userProfile?.exporter_id, speciesId, season]
  );

  useEffect(() => {
    if (!userProfile?.exporter_id || !canLoad) return;
    fetchLabels(userProfile.exporter_id, speciesId, season).catch((error) => {
      toast.error(t('Error al cargar las etiquetas.'));
      console.error(error);
    });
  }, [canLoad, fetchLabels, season, speciesId, t, userProfile?.exporter_id]);

  const resetAddForm = () => {
    setNewLabelName('');
    setNewCategory('ESTANDAR');
  };

  const handleAdd = async () => {
    if (!userProfile?.exporter_id) return;
    const normalizedLabel = newLabelName.trim().toUpperCase();
    if (!normalizedLabel) {
      toast.warn(t('La etiqueta no puede estar vacía.'));
      return;
    }
    const hasDuplicate = labels.some(
      (item) => item.label.trim().toUpperCase() === normalizedLabel
    );
    if (hasDuplicate) {
      toast.warn(t('Ya existe una etiqueta con ese nombre.'));
      return;
    }

    try {
      await addLabel(
        userProfile.exporter_id,
        speciesId,
        season,
        normalizedLabel,
        newCategory
      );
      toast.success(t('Etiqueta añadida con éxito.'));
      resetAddForm();
      onAddClose();
    } catch (error) {
      toast.error(t('Error al añadir la etiqueta.'));
      console.error(error);
    }
  };

  const startEditing = (record: ExporterLabelRecord) => {
    setEditingLabel({ ...record });
    onEditOpen();
  };

  const handleUpdate = async () => {
    if (!editingLabel) return;
    const normalizedLabel = editingLabel.label.trim().toUpperCase();
    if (!normalizedLabel) {
      toast.warn(t('La etiqueta no puede estar vacía.'));
      return;
    }
    const hasDuplicate = labels.some(
      (item) =>
        item.id !== editingLabel.id &&
        item.label.trim().toUpperCase() === normalizedLabel
    );
    if (hasDuplicate) {
      toast.warn(t('Ya existe una etiqueta con ese nombre.'));
      return;
    }

    try {
      await updateLabel(
        editingLabel.id,
        normalizedLabel,
        editingLabel.category
      );
      toast.success(t('Etiqueta actualizada con éxito.'));
      onEditClose();
    } catch (error) {
      toast.error(t('Error al actualizar la etiqueta.'));
      console.error(error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteLabel(id);
      toast.success(t('Etiqueta eliminada con éxito.'));
    } catch (error) {
      toast.error(t('Error al eliminar la etiqueta.'));
      console.error(error);
    }
  };

  return (
    <div>
      <div className='flex items-center gap-4 mb-2'>
        <p className='text-lg font-bold'>{t('Etiquetas Oficiales')}</p>
        <Button
          size='sm'
          color='primary'
          onPress={onAddOpen}
          isDisabled={!canLoad}
        >
          <Icon icon='tabler:plus' />
          {t('Nueva Etiqueta')}
        </Button>
      </div>
      <p className='text-sm text-default-400 mb-4'>
        {t(
          'Define tus etiquetas oficiales por especie y temporada para estandarizar tus liquidaciones.'
        )}
      </p>
      <p className='text-xs text-default-300 mb-4'>
        {t('Contexto actual')}:{' '}
        <span className='font-medium text-default-400'>
          {selectedSpecies?.name || t('Especie no seleccionada')} ·{' '}
          {season || t('Temporada no seleccionada')}
        </span>
      </p>

      {!canLoad ? (
        <div className='text-sm text-default-400'>
          {t(
            'Selecciona una especie y temporada en el sidebar para continuar.'
          )}
        </div>
      ) : loading ? (
        <LoadingState />
      ) : (
        <Table aria-label={t('Tabla de Etiquetas')}>
          <TableHeader>
            <TableColumn>{t('ETIQUETA')}</TableColumn>
            <TableColumn>{t('CATEGORÍA')}</TableColumn>
            <TableColumn>{t('ACCIONES')}</TableColumn>
          </TableHeader>
          <TableBody items={labels}>
            {(item) => (
              <TableRow key={item.id}>
                <TableCell>{item.label}</TableCell>
                <TableCell>
                  <Chip
                    color={item.category === 'PREMIUM' ? 'warning' : 'default'}
                    variant='flat'
                  >
                    {item.category}
                  </Chip>
                </TableCell>
                <TableCell>
                  <div className='flex gap-2'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      onPress={() => startEditing(item)}
                    >
                      <Icon icon='tabler:edit' />
                    </Button>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      onPress={() => handleDelete(item.id)}
                    >
                      <Icon icon='tabler:trash' />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('Editar Etiqueta')}</ModalHeader>
              <ModalBody>
                <Input
                  label={t('Nombre de etiqueta')}
                  value={editingLabel?.label || ''}
                  onChange={(e) => {
                    if (editingLabel) {
                      setEditingLabel({
                        ...editingLabel,
                        label: e.target.value.toUpperCase(),
                      });
                    }
                  }}
                />
                <Select
                  label={t('Categoría')}
                  selectedKeys={
                    editingLabel ? new Set([editingLabel.category]) : undefined
                  }
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] as
                      | LabelCategory
                      | undefined;
                    if (editingLabel && value) {
                      setEditingLabel({ ...editingLabel, category: value });
                    }
                  }}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={onEditClose}>
                  {t('Cancelar')}
                </Button>
                <Button color='primary' onPress={handleUpdate}>
                  {t('Guardar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => {
          resetAddForm();
          onAddClose();
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('Añadir Etiqueta')}</ModalHeader>
              <ModalBody>
                <Input
                  autoFocus
                  label={t('Nombre de etiqueta')}
                  placeholder={t('Ingrese el nombre de la etiqueta')}
                  value={newLabelName}
                  onChange={(e) =>
                    setNewLabelName(e.target.value.toUpperCase())
                  }
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
                <Select
                  label={t('Categoría')}
                  selectedKeys={new Set([newCategory])}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] as
                      | LabelCategory
                      | undefined;
                    if (value) setNewCategory(value);
                  }}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onPress={() => {
                    resetAddForm();
                    onAddClose();
                  }}
                >
                  {t('Cancelar')}
                </Button>
                <Button color='primary' onPress={handleAdd}>
                  {t('Añadir')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};
